import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/api/fetcher';
import { ApiEndpoint } from '@/api/registry';
import type { ScenarioSummary } from '@/features/decisions/scenarios.api.types';
import { useTenant } from '@/providers/TenantProvider';

export interface CreateScenarioInput {
  scenario_run_id: string;
  label: string;
  created_by: string;
}

// The service constrains scenario_run_id to this set because the value escapes the
// database three ways: a URL path segment on four routes, a ClickHouse partition key,
// and the MDS group_id inside S3 object paths. Mirrored here so a bad id is caught
// before a round trip, not because the client is the authority on it.
export const SCENARIO_ID_PATTERN = /^[A-Za-z0-9._-]+$/;
export const SCENARIO_FIELD_MAX_LENGTH = 255;

export function useCreateScenario() {
  const { tenantAlias } = useTenant();
  const queryClient = useQueryClient();

  return useMutation<ScenarioSummary, Error, CreateScenarioInput>({
    mutationFn: (input) =>
      apiFetch<ScenarioSummary>(tenantAlias, ApiEndpoint.ScenarioCreate, {
        body: input,
      }),
    onSuccess: () => {
      // Prefix match, so every cached page of the list is refetched rather than only
      // the one the user happened to be on.
      void queryClient.invalidateQueries({
        queryKey: ['KSAI-DEEP-ENTERPRISE', tenantAlias, ApiEndpoint.ScenariosList],
      });
    },
  });
}
